/**
 * Iter-114 — Header Flag Bell.
 *
 * Persistent attention indicator. Shows the count of ACTIVE flags
 * (pending + acknowledged but NOT yet resolved) directed AT the
 * current user. Click → small dropdown of the most recent active
 * flags + a "View all" link to the full FlagsPage.
 *
 * Visual contract:
 *   - 0 active     → neutral icon, no badge (low noise).
 *   - >0 pending   → strong AMBER triangle + count (high attention).
 *   - 0 pending,
 *     >0 acked     → softer amber dot (still visible, less urgent).
 *
 * Polls every 30 seconds via /flags/pending-count and re-fetches
 * when the bell is opened so the dropdown is always fresh. We DO NOT
 * rely on the iter-78 Recent Activity stream — this is a separate,
 * persistent attention layer per the spec.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { AlertTriangle, Check, Sparkles, RotateCcw, RefreshCcw } from "lucide-react";
import { api } from "../lib/api";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
} from "./ui/dropdown-menu";


function flagEntityHref(routeBase, projectId, flag) {
    // Iter-114 — map flag.entity_type → in-app URL so clicking a row
    // jumps to the exact item. New types should land in this switch.
    // Iter-116 PART 1 — append ?flag={id} so the global FlagPanel in
    // ProjectHeader auto-opens with full context.
    // Iter-158.5.2.a — accept `routeBase` explicitly so the same helper
    // can render URLs for both legacy (`/projects`) and beta
    // (`/beta/hm-app/projects`) namespaces without leaking cross-context.
    const t = flag?.entity_type || "state_entry";
    const eid = flag?.entity_id || flag?.entry_id;
    const fid = flag?.id;
    let base;
    if (!eid) {
        base = `${routeBase}/${projectId}/flags`;
    } else {
        switch (t) {
            case "state_entry":
            case "image_note":
                base = `${routeBase}/${projectId}/state-entries/${eid}`;
                break;
            case "scene":
            case "scene_note":
                base = `${routeBase}/${projectId}/scenes/${eid}`;
                break;
            case "character":
            case "character_note":
                base = `${routeBase}/${projectId}/characters/${eid}`;
                break;
            case "supervisor_note":
            case "script_note":
                base = `${routeBase}/${projectId}/supervisor-notes`;
                break;
            case "continuity_note":
            case "document":
                base = `${routeBase}/${projectId}/supervisor-notes`;
                break;
            case "schedule":
            case "schedule_note":
                base = `${routeBase}/${projectId}/schedule`;
                break;
            default:
                base = `${routeBase}/${projectId}/flags`;
                break;
        }
    }
    return fid ? `${base}?flag=${fid}` : base;
}


function entityLabel(t) {
    switch (t) {
        case "state_entry": return "Image";
        case "image_note": return "Image note";
        case "scene": return "Scene";
        case "scene_note": return "Scene note";
        case "character": return "Cast";
        case "character_note": return "Cast note";
        case "supervisor_note": return "Note";
        case "script_note": return "Script note";
        case "continuity_note": return "Continuity";
        case "document": return "Document";
        case "schedule": return "Schedule";
        case "schedule_note": return "Schedule note";
        default: return t || "Item";
    }
}


// Iter-121 — notification-feed helpers. Small pure functions so the
// browser-Notification and in-dropdown rendering share the exact
// same copy. Actor names default to a neutral fallback so older
// payloads (pre-iter-121) never render "undefined acknowledged…".
function eventTitle(ev) {
    const who = ev.actor_name || "Someone";
    const what = entityLabel(ev.entity_type).toLowerCase();
    switch (ev.kind) {
        case "created": return `${who} flagged a ${what}`;
        case "acknowledged": return `${who} acknowledged your flag`;
        case "resolved": return `${who} resolved a flag`;
        case "reopened": return `${who} re-opened a flag`;
        default: return "Flag update";
    }
}

function eventBody(ev) {
    return ev.note || ev.resolution_note || entityLabel(ev.entity_type);
}


export default function FlagBell({ projectId }) {
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    const { user: me } = useAuth();
    const [counts, setCounts] = useState({ count: 0, pending: 0, active: 0, unacknowledged: 0 });
    const [flags, setFlags] = useState([]);
    const [open, setOpen] = useState(false);
    // Iter-126 — `loading` now only represents the FIRST-LOAD state so
    // subsequent polls / re-opens don't flash "Loading flags…". After
    // the first successful fetch we never flip back to true; the
    // dropdown keeps showing the last known list while a background
    // refresh runs. `listError` surfaces the "Unable to refresh —
    // retry" banner without clearing existing flags.
    const [loading, setLoading] = useState(true);
    const [listError, setListError] = useState("");
    // Monotonic request sequence. Each fetch increments the ref; when
    // the response arrives we ignore it unless its seq matches. This
    // is simpler than AbortController for our case because Motor /
    // axios cancellations can race each other on mobile Safari.
    const listSeqRef = useRef(0);
    const countsSeqRef = useRef(0);
    const eventsSeqRef = useRef(0);
    // Have we ever successfully loaded the list? Drives first-paint
    // skeleton vs keep-what-we-had-on-error behaviour.
    const hasLoadedRef = useRef(false);
    const pollRef = useRef(null);
    const knownFlagIdsRef = useRef(new Set());

    // Iter-121 — Flag notification feed. Tracks ack/resolve events
    // so the FlagBell surfaces them even though the active-flag
    // count alone can't express "someone just resolved your flag".
    // Persisted `lastSeen` per (user, project) in localStorage so
    // reloading the tab doesn't spam old events.
    const notifStorageKey =
        me?.id && projectId ? `osf:flag-notif-seen:${me.id}:${projectId}` : null;
    const [flagEvents, setFlagEvents] = useState([]);
    const lastSeenRef = useRef(null);
    const notifiedIdsRef = useRef(new Set());
    const toastedEventsRef = useRef(new Set());

    const loadLastSeen = useCallback(() => {
        if (!notifStorageKey) return null;
        try {
            return window.localStorage.getItem(notifStorageKey) || null;
        } catch {
            return null;
        }
    }, [notifStorageKey]);

    const persistLastSeen = useCallback((ts) => {
        if (!notifStorageKey || !ts) return;
        try {
            window.localStorage.setItem(notifStorageKey, ts);
        } catch {
            /* ignore quota / private mode errors */
        }
    }, [notifStorageKey]);

    const refreshNotifications = useCallback(async () => {
        if (!projectId) return;
        // Iter-158.13.2.20e.2 — Flags are H&M-scope; Production T&A
        // page uses apiBase="/beta/projects" where flags endpoints
        // are not mounted. Skip to avoid 404 spam on the bell.
        if (apiBase === "/beta/projects") return;
        const seq = ++eventsSeqRef.current;
        try {
            const { data } = await api.get(
                `${apiBase}/${projectId}/flags/notifications`,
                { params: { limit: 25 } },
            );
            // Drop stale responses — only the latest issued request
            // may update state.
            if (seq !== eventsSeqRef.current) return;
            const events = Array.isArray(data?.events) ? data.events : [];
            setFlagEvents(events);

            const lastSeen = lastSeenRef.current || loadLastSeen();
            const hasNativeNotif =
                typeof window !== "undefined" &&
                "Notification" in window &&
                window.Notification.permission === "granted";

            for (const ev of events) {
                const evKey = `${ev.flag_id}:${ev.kind}:${ev.at}`;
                if (lastSeen && ev.at <= lastSeen) continue;
                // Iter-126 — interim in-app alert. Even when the user
                // has NOT granted native Notification permission, we
                // still surface new flag events as a sonner toast so
                // nothing slides by silently. Each event key is only
                // toasted once per session via toastedEventsRef.
                if (!toastedEventsRef.current.has(evKey)) {
                    toastedEventsRef.current.add(evKey);
                    const title = eventTitle(ev);
                    const body = eventBody(ev);
                    try {
                        toast(title, {
                            description: body,
                            duration: 6000,
                        });
                    } catch {
                        /* sonner may not be mounted on very early
                           route transitions — ignore. */
                    }
                }
                // Native Notification popup on top of the toast when
                // permission is granted. Also deduped per session.
                if (hasNativeNotif && !notifiedIdsRef.current.has(evKey)) {
                    notifiedIdsRef.current.add(evKey);
                    try {
                        const n = new window.Notification(eventTitle(ev), {
                            body: eventBody(ev),
                            tag: ev.flag_id,
                            data: {
                                href: flagEntityHref(projectId, {
                                    id: ev.flag_id,
                                    entity_type: ev.entity_type,
                                    entity_id: ev.entity_id,
                                }),
                            },
                        });
                        n.onclick = () => {
                            try {
                                window.focus();
                                window.location.href =
                                    n.data?.href || `${routeBase}/${projectId}/flags`;
                            } catch { /* ignore */ }
                        };
                    } catch {
                        /* Notification constructor can throw on
                           older Safari in private mode — fall back
                           silently to the badge + toast. */
                    }
                }
            }
            // Iter-129 — once we've toasted a batch, advance the
            // persisted lastSeen marker so a reload / re-mount does
            // NOT re-toast the same flags. Previously lastSeen only
            // moved when the user explicitly opened the dropdown,
            // which is why pilots reported flag toasts "repeatedly
            // reappearing". The badge + dropdown counts are untouched
            // — those track unread/unacked state via the API. This
            // only suppresses duplicate toast notifications.
            if (events.length > 0) {
                const latest = events[0]?.at;
                if (latest && (!lastSeenRef.current || latest > lastSeenRef.current)) {
                    lastSeenRef.current = latest;
                    persistLastSeen(latest);
                }
            }
        } catch {
            /* feed is non-critical; badge + dropdown still work */
        }
    }, [projectId, loadLastSeen, persistLastSeen, apiBase, routeBase]);

    const refreshCount = useCallback(async () => {
        if (!projectId) return;
        // Iter-158.13.2.20e.2 — Same skip as refreshNotifications.
        if (apiBase === "/beta/projects") return;
        const seq = ++countsSeqRef.current;
        try {
            const { data } = await api.get(
                `${apiBase}/${projectId}/flags/pending-count?mine=true`
            );
            if (seq !== countsSeqRef.current) return;
            setCounts(data || { count: 0, pending: 0, active: 0 });
        } catch (_) {
            // Silent — header counter is non-critical and may fail
            // briefly during route transitions / cold-starts. Do NOT
            // reset counts to zero on error; keep the last known good
            // value so the badge doesn't flicker-down-then-up.
        }
    }, [projectId, apiBase]);

    const refreshList = useCallback(async () => {
        if (!projectId) return;
        // Iter-158.13.2.20e.2 — Skip Production T&A scope.
        if (apiBase === "/beta/projects") return;
        const seq = ++listSeqRef.current;
        // Iter-126 — only show the skeleton on the FIRST fetch. All
        // subsequent polls / re-opens refresh in the background,
        // keeping the previous list visible.
        if (!hasLoadedRef.current) setLoading(true);
        try {
            const { data } = await api.get(
                `${apiBase}/${projectId}/flags?mine=true`
            );
            if (seq !== listSeqRef.current) return; // stale
            const active = (Array.isArray(data) ? data : []).filter(
                (f) => f && f.status !== "resolved",
            );
            const next = active.slice(0, 8);
            setFlags(next);
            // Track known flag ids so a brand-new flag landing on
            // the next poll can fire an interim toast even if the
            // notifications-feed arrives late.
            const prevKnown = knownFlagIdsRef.current;
            for (const f of next) {
                if (prevKnown.size > 0 && !prevKnown.has(f.id) && f.flagged_by !== me?.id) {
                    const evKey = `flag-created:${f.id}`;
                    if (!toastedEventsRef.current.has(evKey)) {
                        toastedEventsRef.current.add(evKey);
                        try {
                            toast(
                                `${f.flagged_by_name || "Someone"} flagged a ${entityLabel(f.entity_type).toLowerCase()}`,
                                { description: f.note || entityLabel(f.entity_type), duration: 6000 },
                            );
                        } catch { /* ignore */ }
                    }
                }
            }
            knownFlagIdsRef.current = new Set(next.map((f) => f.id));
            hasLoadedRef.current = true;
            setListError("");
        } catch (err) {
            if (seq !== listSeqRef.current) return;
            // Iter-126 — do NOT clear flags on error. Keep the last
            // known list and surface a non-destructive banner with a
            // Retry. Silent clearing was the #1 source of user pain
            // ("it just emptied out while I was looking at it").
            setListError(
                (err && err.message) || "Unable to refresh flags",
            );
        } finally {
            if (seq === listSeqRef.current) setLoading(false);
        }
    }, [projectId, me?.id]);

    // Poll count + events + list every 30s. List polling was added
    // in iter-126 so the dropdown stays fresh in the background even
    // when the bell is closed — this also makes the badge count and
    // list the same source of truth at any moment.
    useEffect(() => {
        refreshList();
        refreshCount();
        refreshNotifications();
        pollRef.current = setInterval(() => {
            refreshList();
            refreshCount();
            refreshNotifications();
        }, 30 * 1000);
        return () => clearInterval(pollRef.current);
    }, [refreshCount, refreshNotifications, refreshList]);

    // Refresh on open — ensures the very first render after a fresh
    // mount shows actual data even if the poll is about to fire.
    useEffect(() => {
        if (open) {
            refreshList();
            refreshCount();
            refreshNotifications();
            if (flagEvents.length > 0) {
                const latest = flagEvents[0]?.at;
                if (latest) {
                    lastSeenRef.current = latest;
                    persistLastSeen(latest);
                }
            }
        }
    }, [open, refreshList, refreshCount, refreshNotifications, flagEvents, persistLastSeen]);

    if (!projectId) return null;

    // Iter-114b — per the refined spec:
    //   * Acknowledge = personal (per-user). Doesn't change global state.
    //   * Resolve = global. The only thing that closes a flag.
    //   * Reopen = global. Re-activates a previously-resolved flag.
    // Bell semantics:
    //   * `counts.unacknowledged` (alias `pending`) > 0 → strong amber
    //     because I personally haven't seen at least one active flag.
    //   * `counts.active` > 0 but unacknowledged === 0 → faded amber
    //     (I've seen them all, but they still need resolution).
    //   * 0 active → clear.
    // Iter-126 — single source of truth. Prefer `/pending-count`
    // response when it's clearly populated, but fall back to the
    // length of the loaded flags list so the badge + dropdown can
    // never disagree (user report: "badge said 3 but dropdown was
    // empty"). We also clamp `unacknowledged` to `active` so the
    // strong-amber variant is never larger than the active total.
    const flagsListLength = flags.length;
    const activeCount = Math.max(
        counts.active ?? counts.count ?? 0,
        flagsListLength,
    );
    const unackedCountRaw = counts.unacknowledged ?? counts.pending ?? 0;
    const unackedCount = Math.min(unackedCountRaw, activeCount);
    const hasActive = activeCount > 0;
    const hasUnacked = unackedCount > 0;
    const visualState = hasUnacked
        ? "pending"
        : hasActive
            ? "acknowledged"
            : "clear";

    // Visual treatments — strong amber for unacknowledged, faded for
    // already-seen-but-not-resolved, muted neutral when clear.
    const triggerClass =
        visualState === "pending"
            ? "tap relative inline-flex items-center justify-center rounded-md h-10 w-10 text-amber-300 hover:text-amber-200 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 ring-1 ring-amber-500/30"
            : visualState === "acknowledged"
                ? "tap relative inline-flex items-center justify-center rounded-md h-10 w-10 text-amber-200/80 hover:text-amber-200 bg-amber-500/8 hover:bg-amber-500/15 border border-amber-500/20"
                : "tap relative inline-flex items-center justify-center rounded-md h-10 w-10 text-neutral-300 hover:text-white hover:bg-white/5";

    // Iter-121 — recent notification events (ack, resolve, reopen,
    // created) that have happened since lastSeen. Used as a tiny
    // secondary badge + inline strip inside the dropdown.
    const lastSeenValue = lastSeenRef.current || loadLastSeen();
    const unreadEvents = flagEvents.filter(
        (e) => !lastSeenValue || e.at > lastSeenValue,
    );
    const unreadCount = unreadEvents.length;

    const canAskForNotifications =
        typeof window !== "undefined" &&
        "Notification" in window &&
        window.Notification.permission === "default";
    const requestNotificationPermission = async () => {
        try {
            await window.Notification.requestPermission();
            // Refresh UI state by forcing a re-render
            refreshNotifications();
        } catch {
            /* user dismissed or browser blocked */
        }
    };

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    data-testid="flag-bell-trigger"
                    data-state={visualState}
                    data-unread-events={unreadCount || 0}
                    title={
                        hasUnacked
                            ? `${unackedCount} flag${unackedCount === 1 ? "" : "s"} need your attention`
                            : hasActive
                                ? `${activeCount} active flag${activeCount === 1 ? "" : "s"} (you've seen them)`
                                : "No active flags"
                    }
                    aria-label="Flags"
                    className={triggerClass}
                >
                    <AlertTriangle size={16} />
                    {hasActive ? (
                        <span
                            data-testid="flag-bell-count"
                            className={`absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full text-[9px] font-bold flex items-center justify-center ${
                                hasUnacked
                                    ? "bg-amber-400 text-neutral-900"
                                    : "bg-amber-300/60 text-neutral-900"
                            }`}
                        >
                            {activeCount > 9 ? "9+" : activeCount}
                        </span>
                    ) : unreadCount > 0 ? (
                        // iter-121 — emerald dot when there's a fresh
                        // event (ack / resolve / reopen) but no active
                        // flags on me. Quiet but visible.
                        <span
                            data-testid="flag-bell-events-dot"
                            className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-400 ring-2 ring-background"
                            aria-label={`${unreadCount} new flag event${unreadCount === 1 ? "" : "s"}`}
                        />
                    ) : null}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                className="w-80 max-w-[90vw] p-0"
                data-testid="flag-bell-dropdown"
            >
                <div className="px-3 pt-3 pb-2 flex items-center justify-between border-b border-white/10">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-400">
                        Flags directed at you
                    </span>
                    <Link
                        to={`${routeBase}/${projectId}/flags`}
                        onClick={() => setOpen(false)}
                        data-testid="flag-bell-view-all"
                        className="text-[11px] text-neutral-300 hover:text-white"
                    >
                        View all
                    </Link>
                </div>

                {/* Iter-126 — explicit "in-app only" status when the
                    user has NOT granted native Notification
                    permission. We never imply push notifications
                    exist; the banner clearly states the current
                    scope. */}
                {canAskForNotifications ? (
                    <div
                        data-testid="flag-bell-notifications-prompt"
                        className="px-3 py-2.5 border-b border-white/10 bg-amber-500/5 flex items-center gap-2"
                    >
                        <AlertTriangle size={14} className="text-amber-300 shrink-0" />
                        <div className="flex-1 min-w-0 text-[11px] text-neutral-300 leading-tight">
                            <span className="block text-[10px] uppercase tracking-[0.15em] text-amber-300/80">
                                In-app only for now
                            </span>
                            Enable device alerts to get notified when you're flagged, acknowledged or resolved.
                        </div>
                        <button
                            type="button"
                            data-testid="flag-bell-enable-notifications"
                            onClick={requestNotificationPermission}
                            className="instant-tap shrink-0 text-[11px] font-semibold text-amber-200 hover:text-amber-100 px-2 h-7 rounded-md bg-amber-500/20 hover:bg-amber-500/30"
                        >
                            Enable
                        </button>
                    </div>
                ) : null}

                {/* Iter-121 — recent events strip. Shows the latest 3
                    ack/resolve/reopen notifications so the user can
                    skim without navigating. Click a row to jump to
                    the flag. */}
                {flagEvents.length > 0 ? (
                    <div
                        data-testid="flag-bell-events"
                        className="px-1 py-1 border-b border-white/10"
                    >
                        <div className="px-2 py-1 text-[9px] uppercase tracking-[0.25em] text-neutral-500">
                            Recent · {flagEvents.length}
                        </div>
                        <ul>
                            {flagEvents.slice(0, 3).map((ev) => {
                                const href = flagEntityHref(routeBase, projectId, {
                                    id: ev.flag_id,
                                    entity_type: ev.entity_type,
                                    entity_id: ev.entity_id,
                                });
                                const isUnread =
                                    !lastSeenValue || ev.at > lastSeenValue;
                                return (
                                    <li
                                        key={`${ev.flag_id}-${ev.kind}-${ev.at}`}
                                        data-testid={`flag-bell-event-${ev.flag_id}-${ev.kind}`}
                                    >
                                        <Link
                                            to={href}
                                            onClick={() => setOpen(false)}
                                            className="block px-2 py-1.5 hover:bg-white/5 rounded-md"
                                        >
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className={`h-1.5 w-1.5 rounded-full shrink-0 ${
                                                        isUnread
                                                            ? "bg-emerald-400"
                                                            : "bg-neutral-700"
                                                    }`}
                                                />
                                                <div className="flex-1 min-w-0 text-[11px] leading-tight">
                                                    <div className="truncate text-neutral-200">
                                                        {eventTitle(ev)}
                                                    </div>
                                                    {eventBody(ev) ? (
                                                        <div className="truncate text-neutral-500 text-[10px]">
                                                            {eventBody(ev)}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ) : null}
                <div className="max-h-80 overflow-y-auto">
                    {/* Iter-126 — non-destructive refresh error. Stays
                        on top of any previously loaded list so the
                        user can still read what they had while tapping
                        Retry to try again. */}
                    {listError ? (
                        <button
                            type="button"
                            data-testid="flag-bell-list-retry"
                            onClick={() => {
                                setListError("");
                                refreshList();
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 border-b border-white/5 bg-red-500/5 text-[11px] text-red-300 hover:bg-red-500/10"
                        >
                            <RefreshCcw size={12} />
                            Unable to refresh flags — tap to retry
                        </button>
                    ) : null}
                    {loading && !hasLoadedRef.current ? (
                        <p
                            data-testid="flag-bell-loading"
                            className="text-xs text-neutral-500 px-3 py-4 text-center"
                        >
                            Loading flags…
                        </p>
                    ) : flags.length === 0 ? (
                        <div className="px-3 py-6 text-center">
                            <Sparkles size={14} className="mx-auto text-neutral-500" />
                            <p className="text-xs text-neutral-400 mt-2">
                                You're all caught up.
                            </p>
                        </div>
                    ) : (
                        <ul data-testid="flag-bell-list">
                            {flags.map((f) => {
                                const href = flagEntityHref(routeBase, projectId, f);
                                const ackBy = Array.isArray(f.acknowledged_by)
                                    ? f.acknowledged_by
                                    : [];
                                const iHaveAcked = ackBy.includes(me?.id);
                                const isReopened = f.status === "reopened";
                                return (
                                    <li
                                        key={f.id}
                                        data-testid={`flag-bell-row-${f.id}`}
                                    >
                                        <Link
                                            to={href}
                                            onClick={() => setOpen(false)}
                                            className="block px-3 py-2.5 hover:bg-white/5 border-b border-white/5"
                                        >
                                            <div className="flex items-start gap-2">
                                                <span
                                                    className={`mt-0.5 h-2 w-2 rounded-full shrink-0 ${
                                                        iHaveAcked
                                                            ? "bg-amber-300/40"
                                                            : "bg-amber-400"
                                                    }`}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
                                                        <span className="font-semibold text-white truncate">
                                                            {f.flagged_by_name || "Someone"}
                                                        </span>
                                                        <span className="text-neutral-500">
                                                            ·
                                                        </span>
                                                        <span className="text-neutral-400">
                                                            {entityLabel(f.entity_type)}
                                                        </span>
                                                        {isReopened ? (
                                                            <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-[0.15em] text-amber-300">
                                                                <RotateCcw size={9} /> reopened
                                                            </span>
                                                        ) : null}
                                                        {iHaveAcked ? (
                                                            <span className="ml-auto inline-flex items-center gap-0.5 text-[9px] uppercase tracking-[0.15em] text-emerald-300/80">
                                                                <Check size={9} /> seen by you
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    {f.note ? (
                                                        <p className="text-xs text-neutral-300 mt-1 line-clamp-2">
                                                            {f.note}
                                                        </p>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
